import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { SectionLabel } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CRAFT = "https://images.unsplash.com/photo-1644258676710-ffb99d7d7a1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const HANDS = "https://images.unsplash.com/photo-1599694522028-65abc96dfd2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const TOOLS = "https://images.unsplash.com/photo-1759523069474-3c45494a6679?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export function Journal() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="journal-page">
      <SectionLabel>The Journal</SectionLabel>
      <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">Style guide &amp; craft notes</h1>
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
        {posts.map((p, i) => (
          <Link key={p.id} to={`/journal/${p.slug}`} data-testid={`journal-card-${p.slug}`} className="group animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="aspect-[3/2] overflow-hidden bg-luxa-sand">
              <img src={p.cover_image} alt={p.title} className="img-zoom w-full h-full object-cover" />
            </div>
            <p className="overline text-luxa-espresso/40 mt-5">{p.created_at?.slice(0, 10)}</p>
            <h2 className="mt-2 font-serif text-2xl group-hover:text-luxa-gold transition-colors duration-300">{p.title}</h2>
            <p className="mt-3 text-sm text-luxa-espresso/62 leading-relaxed">{p.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function JournalPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  if (p === false) return <div className="py-32 text-center font-serif text-2xl">Article not found.</div>;
  if (!p) return <div className="py-32 text-center overline text-luxa-espresso/40">Loading…</div>;
  return (
    <article className="max-w-3xl mx-auto px-5 py-16 lg:py-24" data-testid="journal-post-page">
      <p className="overline text-luxa-brown">{p.author} · {p.created_at?.slice(0, 10)}</p>
      <h1 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tighter leading-none">{p.title}</h1>
      <div className="mt-10 aspect-[16/9] overflow-hidden bg-luxa-sand">
        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
      </div>
      <div className="mt-12 space-y-6 text-lg leading-relaxed text-luxa-espresso/80">
        {p.body.split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
      </div>
      <Link to="/journal" className="mt-14 inline-block overline text-luxa-brown link-underline">← All articles</Link>
    </article>
  );
}

export function About() {
  return (
    <div data-testid="about-page">
      <div className="relative h-[52vh] overflow-hidden grain">
        <img src={CRAFT} alt="Atelier" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-luxa-espresso/40 flex items-end">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-14 w-full">
            <p className="overline text-luxa-gold">About the house</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-luxa-cream leading-none">
              A small atelier,<br />an unfashionable pace.
            </h1>
          </div>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-[1.2fr_1fr] gap-16">
        <div className="space-y-6 text-lg leading-relaxed text-luxa-espresso/80">
          <p className="font-serif text-2xl sm:text-3xl tracking-tight leading-snug text-luxa-espresso">
            Trust Luxa is a premium leather house built on trust, durability and elegance — 100% leather products
            designed to last for years.
          </p>
          <p>The brand exists at the meeting point of traditional leather craftsmanship and modern luxury design: objects that improve with age and develop a character no two owners will ever share.</p>
          <p>We work only with full-grain, vegetable-tanned hides from tanneries that can tell us which batch a panel came from. Nothing is bonded, corrected, or coated to hide a weak grain.</p>
          <p>Each piece is made start to finish by one maker, whose initials go on the inside of the last panel. If a stitch ever fails, that same bench repairs it — for ten years, at no cost.</p>
          <p>We release in small runs. When a hide is finished, the run is finished. It is a slower way to build a brand, and the only way we know how to build an object worth keeping.</p>
        </div>
        <div className="space-y-6">
          <img src={HANDS} alt="Hand stitching" className="w-full aspect-[4/5] object-cover" />
          <div className="border border-luxa-sand p-7 bg-luxa-shell">
            <p className="overline text-luxa-brown">The promise</p>
            <p className="mt-4 font-serif text-2xl leading-snug">Ten years of repairs on every stitch we make.</p>
          </div>
        </div>
      </div>

      <section className="bg-luxa-espresso text-luxa-cream -mb-32" data-testid="mission-vision">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 lg:gap-24">
          {[["Mission", "To create premium 100% leather products that combine timeless craftsmanship, luxury design and lasting durability — while building trust with every customer."],
            ["Vision", "To become a globally recognised luxury leather brand known for authenticity, craftsmanship and timeless design."]].map(([label, copy]) => (
            <div key={label} className="border-t border-luxa-gold/40 pt-8">
              <p className="overline text-luxa-gold">{label}</p>
              <p className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-snug">{copy}</p>
            </div>
          ))}
        </div>
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20 grid sm:grid-cols-3 gap-10">
          {[["Trust", "Every claim we make about a hide can be traced to the tannery that supplied it."],
            ["Durability", "Saddle-stitched seams and resoleable construction, backed by a ten-year repair promise."],
            ["Elegance", "Restrained shapes and hand-burnished edges, made to look considered a decade from now."]].map(([t, d]) => (
            <div key={t}>
              <span aria-hidden="true" className="block w-8 h-px bg-luxa-gold" />
              <p className="mt-5 font-serif text-xl">{t}</p>
              <p className="mt-3 text-sm leading-relaxed text-luxa-cream/60">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Craftsmanship() {
  const steps = [
    ["Selecting the hide", "We buy whole hides, not pre-cut panels, so we can place every piece where the grain is strongest."],
    ["Cutting by hand", "A single-panel cut with a round knife. No fillers, no bonded backing, no shortcuts under the lining."],
    ["Skiving & folding", "Edges are thinned by hand so folds sit flat without bulk — the difference between slim and merely thin."],
    ["Saddle stitching", "Two needles, one waxed linen thread, slanted awl holes. Cut one stitch and the seam still holds."],
    ["Burnishing", "Bevelled, sanded, dampened and burnished with hardwood across three passes, then waxed."],
    ["Final inspection", "Every piece is stamped by its maker and inspected against a ten-year repair standard."],
  ];
  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24" data-testid="craftsmanship-page">
      <SectionLabel>Craftsmanship</SectionLabel>
      <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none max-w-3xl">
        Six stages between a hide and an heirloom
      </h1>
      <p className="mt-7 text-luxa-espresso/60 max-w-2xl text-lg leading-relaxed">
        Traditional craftsmanship, held to a modern luxury standard. This is the sequence every Trust Luxa
        piece passes through before it earns our name.
      </p>
      <div className="mt-16 grid lg:grid-cols-[1fr_1.1fr] gap-16 items-start">
        <div className="lg:sticky lg:top-28 space-y-6">
          <img src={TOOLS} alt="Leather tools" className="w-full aspect-[4/3] object-cover" />
          <img src={HANDS} alt="Stitching" className="w-full aspect-[4/3] object-cover" />
        </div>
        <ol className="divide-y divide-luxa-espresso/12">
          {steps.map(([t, d], i) => (
            <li key={t} className="py-8 flex gap-7">
              <span className="font-serif text-3xl text-luxa-gold">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h2 className="font-serif text-2xl">{t}</h2>
                <p className="mt-3 text-luxa-espresso/70 leading-relaxed">{d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/contact", f); toast.success("Message received. We reply within two business days."); setF({ name: "", email: "", message: "" }); }
    catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };
  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-16" data-testid="contact-page">
      <div>
        <SectionLabel>Contact</SectionLabel>
        <h1 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tighter leading-none">Talk to the atelier</h1>
        <p className="mt-7 text-luxa-espresso/70 leading-relaxed max-w-md">
          Questions on sizing, repairs, corporate gifting or a bespoke commission — write to us and a maker replies, not a script.
        </p>
        <div className="mt-10 space-y-5">
          {[["Care & repairs", "care@trustluxa.com"], ["Orders", "orders@trustluxa.com"], ["Bespoke & gifting", "atelier@trustluxa.com"]].map(([l, v]) => (
            <div key={l}><p className="overline text-luxa-espresso/45">{l}</p><p className="font-serif text-xl mt-1">{v}</p></div>
          ))}
        </div>
      </div>
      <form onSubmit={submit} className="bg-luxa-shell border border-luxa-sand p-8 space-y-5 h-fit" data-testid="contact-form">
        <div>
          <Label className="overline text-luxa-espresso/50">Name</Label>
          <Input data-testid="contact-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
            className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-12" />
        </div>
        <div>
          <Label className="overline text-luxa-espresso/50">Email</Label>
          <Input data-testid="contact-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
            className="mt-2 rounded-none bg-luxa-shell border-luxa-sand h-12" />
        </div>
        <div>
          <Label className="overline text-luxa-espresso/50">Message</Label>
          <Textarea data-testid="contact-message" required rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
            className="mt-2 rounded-none bg-luxa-shell border-luxa-sand" />
        </div>
        <Button data-testid="contact-submit" type="submit" className="w-full rounded-none bg-luxa-brown hover:bg-luxa-brown/90 text-white h-12 overline">Send Message</Button>
      </form>
    </div>
  );
}
